import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import {
  getAllUsers,
  getUserDetails,
  getUserLinkedContent,
  banUserAccount,
  unbanUserAccount,
} from '@/services/userAdmin';
import { isApiError, getErrorMessage } from '@/services/api/errors';
import type { PaginationInfo } from '@/services/api/types';
import type { UserProfile, UserProfileComplete, LinkedContent } from '@/types/userAdmin';

interface UseUserAdministrationState {
  users: UserProfile[];
  selectedUser: UserProfileComplete | null;
  linkedContent: LinkedContent | null;
  loading: boolean;
  loadingUsers: boolean;
  loadingDetails: boolean;
  error: string | null;
  pagination: PaginationInfo | null;
}

interface UseUserAdministrationActions {
  selectUser: (userId: string) => Promise<void>;
  clearSelectedUser: () => void;
  refreshUsers: () => Promise<void>;
  refreshSelectedUser: () => Promise<void>;
  /** @deprecated Use banAccount instead */
  disableAccount: (userId: string) => Promise<void>;
  /** @deprecated Use unbanAccount instead */
  enableAccount: (userId: string) => Promise<void>;
  banAccount: (userId: string, reason?: string) => Promise<void>;
  unbanAccount: (userId: string) => Promise<void>;
  loadMoreUsers: () => Promise<void>;
  searchUsers: (query: string) => Promise<void>;
  clearError: () => void;
}

type UseUserAdministrationReturn = UseUserAdministrationState & UseUserAdministrationActions;

/**
 * Custom hook for user administration functionality
 * Manages state for user list, selected user details, and account actions
 * Updated for 007-cloudflare-migration with API Gateway support
 *
 * @returns {UseUserAdministrationReturn} State and actions for user administration
 *
 * @example
 * const { users, selectedUser, selectUser, disableAccount } = useUserAdministration();
 */
export function useUserAdministration(): UseUserAdministrationReturn {
  const { t } = useTranslation('common');
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserProfileComplete | null>(null);
  const [linkedContent, setLinkedContent] = useState<LinkedContent | null>(null);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const getLocalizedError = useCallback(
    (err: unknown): string => {
      if (isApiError(err)) {
        return getErrorMessage(err, t);
      }
      return err instanceof Error ? err.message : t('api.errors.unknown');
    },
    [t]
  );

  // Load all users on mount
  useEffect(() => {
    loadUsers();
  }, []);

  /**
   * Load all users from the API
   */
  const loadUsers = useCallback(async (query?: string, cursor?: string) => {
    setLoadingUsers(true);
    setError(null);

    try {
      const { data: fetchedUsers, error: fetchError, pagination: paginationInfo } = await getAllUsers({
        q: query,
        cursor,
      });

      if (fetchError) {
        const errorMessage = getLocalizedError(fetchError);
        setError(errorMessage);
        if (!isApiError(fetchError)) {
          Sentry.captureException(fetchError, {
            tags: { component: 'useUserAdministration', action: 'loadUsers' },
          });
        }
      } else if (fetchedUsers) {
        if (cursor) {
          // Append to existing users for pagination
          setUsers((prev) => [...prev, ...fetchedUsers]);
        } else {
          // Replace users for fresh load or search
          setUsers(fetchedUsers);
        }
        setPagination(paginationInfo);
      }
    } catch (err) {
      const errorMessage = getLocalizedError(err);
      setError(errorMessage);
      if (!isApiError(err)) {
        Sentry.captureException(err, {
          tags: { component: 'useUserAdministration', action: 'loadUsers' },
        });
      }
    } finally {
      setLoadingUsers(false);
    }
  }, [getLocalizedError]);

  /**
   * Select a user and load their complete details
   */
  const selectUser = useCallback(async (userId: string) => {
    setLoadingDetails(true);
    setError(null);

    try {
      // Load user profile details
      const { data: profile, error: profileError } = await getUserDetails(userId);

      if (profileError) {
        const errorMessage = getLocalizedError(profileError);
        setError(errorMessage);
        if (!isApiError(profileError)) {
          Sentry.captureException(profileError, {
            tags: { component: 'useUserAdministration', action: 'selectUser' },
            user: { id: userId },
          });
        }
        setSelectedUser(null);
        setLinkedContent(null);
        return;
      }

      if (profile) {
        setSelectedUser(profile);
      }

      // Load linked content
      try {
        const content = await getUserLinkedContent(userId);
        setLinkedContent(content);
      } catch (contentErr) {
        // Don't fail the whole operation if linked content fails
        console.warn('Failed to load linked content:', contentErr);
        Sentry.addBreadcrumb({
          category: 'user-admin',
          message: 'Failed to load linked content',
          level: 'warning',
          data: { userId },
        });
        setLinkedContent(null);
      }
    } catch (err) {
      const errorMessage = getLocalizedError(err);
      setError(errorMessage);
      if (!isApiError(err)) {
        Sentry.captureException(err, {
          tags: { component: 'useUserAdministration', action: 'selectUser' },
          user: { id: userId },
        });
      }
      setSelectedUser(null);
      setLinkedContent(null);
    } finally {
      setLoadingDetails(false);
    }
  }, [getLocalizedError]);

  /**
   * Clear selected user (back to list)
   */
  const clearSelectedUser = useCallback(() => {
    setSelectedUser(null);
    setLinkedContent(null);
  }, []);

  /**
   * Refresh the user list
   */
  const refreshUsers = useCallback(async () => {
    await loadUsers(searchQuery);
  }, [loadUsers, searchQuery]);

  /**
   * Refresh the currently selected user's details
   */
  const refreshSelectedUser = useCallback(async () => {
    if (selectedUser) {
      await selectUser(selectedUser.id);
    }
  }, [selectedUser, selectUser]);

  /**
   * Load more users (cursor-based pagination)
   */
  const loadMoreUsers = useCallback(async () => {
    if (pagination?.hasMore && pagination.cursor) {
      await loadUsers(searchQuery, pagination.cursor);
    }
  }, [loadUsers, pagination, searchQuery]);

  /**
   * Search users by query
   */
  const searchUsers = useCallback(async (query: string) => {
    setSearchQuery(query);
    await loadUsers(query);
  }, [loadUsers]);

  /**
   * Disable a user account
   * @deprecated Use banAccount instead
   */
  const disableAccount = useCallback(async (userId: string) => {
    return banAccount(userId);
  }, []);

  /**
   * Enable a user account
   * @deprecated Use unbanAccount instead
   */
  const enableAccount = useCallback(async (userId: string) => {
    return unbanAccount(userId);
  }, []);

  /**
   * Ban a user account
   * @param userId - The user ID to ban
   * @param reason - Optional reason for the ban
   */
  const banAccount = useCallback(async (userId: string, reason?: string) => {
    setError(null);

    try {
      const { data: updatedProfile, error: banError } = await banUserAccount(userId, reason);

      if (banError) {
        const errorMessage = getLocalizedError(banError);
        setError(errorMessage);
        if (!isApiError(banError)) {
          Sentry.captureException(banError, {
            tags: { component: 'useUserAdministration', action: 'banAccount' },
            user: { id: userId },
          });
        }
        throw banError;
      }

      // Update local state directly with the returned data
      if (updatedProfile) {
        // Update user in the list
        setUsers((prev) =>
          prev.map((user) =>
            user.id === userId
              ? { ...user, status: 'banned', ban_reason: reason ?? null }
              : user
          )
        );

        // Update selected user if it's the same one
        if (selectedUser?.id === userId) {
          setSelectedUser(updatedProfile);
        }
      }
    } catch (err) {
      const errorMessage = getLocalizedError(err);
      setError(errorMessage);
      throw err;
    }
  }, [selectedUser, getLocalizedError]);

  /**
   * Unban a user account
   */
  const unbanAccount = useCallback(async (userId: string) => {
    setError(null);

    try {
      const { data: updatedProfile, error: unbanError } = await unbanUserAccount(userId);

      if (unbanError) {
        const errorMessage = getLocalizedError(unbanError);
        setError(errorMessage);
        if (!isApiError(unbanError)) {
          Sentry.captureException(unbanError, {
            tags: { component: 'useUserAdministration', action: 'unbanAccount' },
            user: { id: userId },
          });
        }
        throw unbanError;
      }

      // Update local state directly with the returned data
      if (updatedProfile) {
        // Update user in the list (clear ban fields)
        setUsers((prev) =>
          prev.map((user) =>
            user.id === userId
              ? { ...user, status: 'active', ban_reason: null, ban_expires: null }
              : user
          )
        );

        // Update selected user if it's the same one
        if (selectedUser?.id === userId) {
          setSelectedUser(updatedProfile);
        }
      }
    } catch (err) {
      const errorMessage = getLocalizedError(err);
      setError(errorMessage);
      throw err;
    }
  }, [selectedUser, getLocalizedError]);

  /**
   * Clear the current error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    users,
    selectedUser,
    linkedContent,
    loading: loadingUsers || loadingDetails,
    loadingUsers,
    loadingDetails,
    error,
    pagination,
    selectUser,
    clearSelectedUser,
    refreshUsers,
    refreshSelectedUser,
    disableAccount,
    enableAccount,
    banAccount,
    unbanAccount,
    loadMoreUsers,
    searchUsers,
    clearError,
  };
}
